import { Component, DestroyRef, EventEmitter, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeUtil, BeUtil } from '../../../hezky-routes';
import { debounceTime } from 'rxjs/operators';
import { Subject } from 'rxjs';

interface NotificationItem {
  notification_id: number;
  notification_type: string;
  notification: string;
  created_at: any;
  ismarked: boolean;
  application_id?: number;
  application_name?: string;
  routing_id?: number | null;
}
interface GroupedNotifications {
  date: string;
  items: NotificationItem[];
}
interface Payload {
  pageType: number;
  dataLimit: number;
}

@Component({
  selector: 'app-notification',
  templateUrl: './notification.component.html',
  styleUrl: './notification.component.scss'
})
export class NotificationComponent {
  @Output() markRead = new EventEmitter<boolean>();
  @Output() onUnreadCountUpdate = new EventEmitter<number>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly scrollSubject = new Subject<Event>();
  private loadedCount = 0;

  allNotifications: GroupedNotifications[] = [];
  unreadNotifications: GroupedNotifications[] = [];
  totalNotifications = 0;
  unreadCount = 0;
  initLoading = false;
  currentFilter: 'all' | 'unread' = 'all';
  hasMoreData = true;
  payload: Payload = { pageType: 1, dataLimit: 50 };

  constructor(protected feUtil: FeUtil, protected beUtil: BeUtil) {}

  get activeNotifications(): GroupedNotifications[] {
    return this.currentFilter === 'all' ? this.allNotifications : this.unreadNotifications;
  }

  ngOnInit() {
    this.getAllNotification();
    this.scrollSubject.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((event: Event) => this.handleScroll(event));
  }

  getAllNotification() {
    this.initLoading = true;
    this.beUtil.notificationSvc.getAllNotifications(this.payload).subscribe({
      next: (response: any) => {
        if (response?.status === 200 && response?.data?.notifications?.length > 0) {
          const batch: NotificationItem[] = response.data.notifications;
          const unreadBatch = batch.filter((item) => item.ismarked === false || item.ismarked === null);
          this.allNotifications = this.mergeGroupedNotifications(this.allNotifications, this.groupNotificationsByDate(batch));
          this.unreadNotifications = this.mergeGroupedNotifications(this.unreadNotifications, this.groupNotificationsByDate(unreadBatch));
          this.totalNotifications = response.data.total_count;
          this.unreadCount = response.data.total_unread;
          this.loadedCount += batch.length;
          this.hasMoreData = this.loadedCount < this.totalNotifications;
          this.onUnreadCountUpdate.emit(this.unreadCount);
          this.beUtil.notificationSvc.updateNotificationCount(this.unreadCount);
        } else {
          this.hasMoreData = false;
        }
        this.initLoading = false;
      },
      error: () => {
        this.initLoading = false;
        this.hasMoreData = false;
      }
    });
  }

  groupNotificationsByDate(notifications: NotificationItem[]): GroupedNotifications[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const groups = new Map<string, NotificationItem[]>();
    [...notifications]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .forEach(notification => {
        const date = this.getDateGroup(notification.created_at, today);
        if (!groups.has(date)) groups.set(date, []);
        groups.get(date)!.push(notification);
      });
    return Array.from(groups.entries())
      .sort(([a], [b]) => {
        if (a === 'Today') return -1;
        if (b === 'Today') return 1;
        if (a === 'Yesterday') return -1;
        if (b === 'Yesterday') return 1;
        return new Date(b).getTime() - new Date(a).getTime();
      })
      .map(([date, items]) => ({ date, items }));
  }

  mergeGroupedNotifications(existing: GroupedNotifications[], incoming: GroupedNotifications[]): GroupedNotifications[] {
    const mergedMap = new Map<string, NotificationItem[]>(
      existing.map(g => [g.date, [...g.items]])
    );
    incoming.forEach(({ date, items }) => {
      if (mergedMap.has(date)) {
        mergedMap.get(date)!.push(...items);
      } else {
        mergedMap.set(date, items);
      }
    });
    return Array.from(mergedMap.entries()).map(([date, items]) => ({ date, items }));
  }

  getDateGroup(date: string, today: Date): string {
    const notifDate = new Date(date);
    notifDate.setHours(0, 0, 0, 0);
    const diffDays = Math.round((today.getTime() - notifDate.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return notifDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  }

  routeToComponent(item: NotificationItem): void {
    this.handleRoute(item);
    this.markAsRead(item);
    this.markRead.emit(true);
  }

  changeFilter(filter: 'all' | 'unread') {
    this.currentFilter = filter;
  }

  handleRoute(item: NotificationItem) {
    if (!item.routing_id) return;
    const appNameLower = item.application_name?.toLowerCase();
    if (item.application_id) this.feUtil.switchApplication(item.application_id);
    if (appNameLower === 'storage') {
      this.feUtil.router.navigate([`/storage-incidents/list/${item.routing_id}`]);
    } else if (appNameLower === 'transport') {
      this.feUtil.router.navigate([`/transport-incidents/list/${item.routing_id}`]);
    }
  }

  markAllAsRead() {
    if (this.unreadCount === 0) {
      this.feUtil.message.info('All notifications are already marked as read.');
      return;
    }
    this.markNotificationAsRead('All');
  }

  markNotificationAsRead(data: NotificationItem | 'All') {
    const requestData = {
      notification_id: data === 'All' ? [] : [data.notification_id],
      mark_all: data === 'All',
    };
    this.beUtil.notificationSvc.markNotificationAsRead(requestData).subscribe({
      next: ({ status }: { status: number }) => {
        if (status === 200) this.refreshNotifications();
      }
    });
  }

  refreshNotifications() {
    this.allNotifications = [];
    this.unreadNotifications = [];
    this.loadedCount = 0;
    this.payload.pageType = 1;
    this.getAllNotification();
  }

  markAsRead(item: NotificationItem) {
    this.markNotificationAsRead(item);
  }

  onScroll(event: Event): void {
    this.scrollSubject.next(event);
  }

  trackByDate(_: number, group: GroupedNotifications): string {
    return group.date;
  }

  trackByNotificationId(_: number, item: NotificationItem): number {
    return item.notification_id;
  }

  private handleScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const scrollThreshold = target.scrollHeight - 200;
    if (scrollPosition >= scrollThreshold && this.hasMoreData && !this.initLoading) {
      this.payload.pageType += 1;
      this.getAllNotification();
    }
  }
}
